import type { CheckoutPropsPurchase } from '@trmediaab/checkout';
import { Checkout } from '@trmediaab/checkout';
import { Box, Button, Heading, Rhythm, Text } from '@trmediaab/zebra';
import { Html } from '@trmediaab/zebra-components';
import { useToggle } from '@trmediaab/zebra-hooks';
import { Chevron } from '@trmediaab/zebra-icons';
import type { Campaign, Product } from '@trmediaab/zebra-types/trhub';

import ProductBulletList from '../ProductBulletList';

export type ProductOfferProps = Omit<
  CheckoutPropsPurchase,
  'mode' | 'containerVariant' | 'sku' | 'campaign'
> &
  (
    | {
        campaign: Campaign;
        product?: never;
      }
    | {
        campaign?: never;
        product: Product;
      }
    | {
        campaign: Campaign;
        product: Product;
      }
  );

const ProductOffer = ({ campaign, product, ...props }: ProductOfferProps) => {
  const [showBullets, toggleBullets] = useToggle();
  const productData = campaign != null ? campaign.products[0] : product;

  const {
    headline: productHeadline,
    infoText: productDescription,
    bullets,
    sku,
  } = productData;
  const {
    headline: campaignHeadline,
    infoText: campaignDescription,
    terms: campaignTerms,
  } = campaign || {};
  const headline = campaignHeadline || productHeadline;
  const description = campaignDescription || productDescription;

  const checkoutProps: CheckoutPropsPurchase =
    campaign != null ? { campaign: campaign._id, ...props } : { sku, ...props };

  return (
    <Rhythm
      bg="offers.backgroundComplementary"
      between="5"
      py="4"
      px={{ _: 3, lg: 5 }}
    >
      <Rhythm
        between="4"
        maxWidth="470px"
        mx="auto"
        sx={{ textAlign: 'center' }}
      >
        <Heading.h2 variant="paywallOffer">{headline}</Heading.h2>

        <Box maxWidth="340px" mx="auto">
          {campaign != null && <Text.strong>{productHeadline}</Text.strong>}
          <Html between="2" variant="checkoutSmall" html={description} />
        </Box>

        <Button
          color="link.primary"
          fontWeight="medium"
          onClick={toggleBullets}
        >
          Visa allt som ingår
          <Chevron size="18px" direction={showBullets ? 'up' : 'down'} />
        </Button>

        {showBullets && (
          <Box maxWidth="340px" mx="auto" pl={{ _: 3, lg: 4 }}>
            <ProductBulletList items={bullets} />
          </Box>
        )}
      </Rhythm>

      <Checkout mode="purchase" containerVariant="narrow" {...checkoutProps} />

      {campaign != null && (
        <Text
          color="text.faded"
          maxWidth="470px"
          mx="auto"
          fontSize="0"
          textAlign="center"
        >
          {campaignTerms}
        </Text>
      )}
    </Rhythm>
  );
};

export default ProductOffer;
